import { Task as ApiTask } from "models/schemas";
import {
  TaskSuggestionGroup,
  TaskSuggestion,
} from "components/task-suggestions/definitions";
import { toUnixEpochTime, normalizeDateFormat } from "utils/date-format";

/**
 * Split tasks into batches to avoid payload size limits
 * @param tasks - Array of tasks to split
 * @param batchSize - Maximum number of tasks per batch
 * @returns Array of task batches
 */
const splitTasksIntoBatches = (
  tasks: TaskSuggestion[],
  batchSize: number = 8
): TaskSuggestion[][] => {
  const batches: TaskSuggestion[][] = [];
  for (let i = 0; i < tasks.length; i += batchSize) {
    batches.push(tasks.slice(i, i + batchSize));
  }
  return batches;
};

/**
 * Create multiple payloads for CreateTasks API with task batching
 * @param taskGroups - Array of task groups from TaskSuggestions component
 * @param parentId - Parent ID (audit ID or shortId)
 * @param teamName - Team name for the tasks
 * @param owner - Owner of the tasks
 * @returns Array of CreateTasks API payloads for sequential execution
 */
export const createTasksPayloads = (
  taskGroups: TaskSuggestionGroup[],
  parentId: string,
  teamName: string,
  owner: string,
  includeOrder: boolean = true
) => {
  const payloads: any[] = [];

  taskGroups.forEach((taskGroup) => {
    const taskGroupId = `TSK-${crypto.randomUUID()}`;
    const taskGroupEntry: ApiTask = {
      taskId: taskGroupId,
      teamName: teamName,
      taskType: "Task Group",
      parentTaskId: parentId,
      taskName: taskGroup.name,
      taskGroupType: taskGroup.type || "Audit Conclusion",
      owners: owner,
      status: "Not started",
      plannedStartDate: undefined,
      dueDate: taskGroup.dueDate
        ? toUnixEpochTime(normalizeDateFormat(taskGroup.dueDate))
        : 0,
      actualStartDate: undefined,
      actualCompletionDate: undefined,
      deleted: false,
      ...(includeOrder && {
        order: taskGroup.order != null ? Number(taskGroup.order) : 0,
      }),
    } as any;
    const taskBatches = splitTasksIntoBatches(taskGroup.tasks, 8);
    if (taskBatches.length === 0) {
      const payload = {
        parentId: parentId,
        teamName: teamName,
        owner: owner,
        tasks: {
          taskList: [taskGroupEntry],
        },
      };
      payloads.push(payload);
    } else {
      taskBatches.forEach((taskBatch, batchIndex) => {
        const batchTasks: ApiTask[] = [];
        if (batchIndex === 0) {
          batchTasks.push(taskGroupEntry);
        }
        taskBatch.forEach((task: TaskSuggestion) => {
          const taskId = `TSK-${crypto.randomUUID()}`;
          const taskEntry: ApiTask = {
            taskId: taskId,
            taskName: task.name,
            owners: task.owners,
            status: "Not started",
            taskType: "Task",
            parentTaskId: taskGroupId,
            teamName: teamName,
            actualStartDate: task.actualStart
              ? toUnixEpochTime(normalizeDateFormat(task.actualStart))
              : 0,
            actualCompletionDate: task.completionDate
              ? toUnixEpochTime(normalizeDateFormat(task.completionDate))
              : 0,
            ...(task.description && { description: task.description }),
            ...(task.type && { inquiryType: task.type }),
            ...(task.complexity && { complexity: task.complexity }),
            ...(task.priority && { priority: task.priority }),
            ...(task.milestone && { milestone: task.milestone }),
            plannedStartDate: task.plannedStart
              ? toUnixEpochTime(normalizeDateFormat(task.plannedStart))
              : 0,
            dueDate: task.dueDate
              ? toUnixEpochTime(normalizeDateFormat(task.dueDate))
              : 0,
            ...(includeOrder && {
              order: task.order != null ? Number(task.order) : 0,
            }),
          } as any;

          batchTasks.push(taskEntry);
        });
        const payload = {
          parentId: parentId,
          teamName: teamName,
          owner: owner,
          tasks: {
            taskList: batchTasks,
          },
        };
        payloads.push(payload);
      });
    }
  });

  return payloads;
};
